import React, {
  JSX,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useUser } from "../utils/auth";
import {
  NotFoundError,
  PermissionDeniedError,
  UnavailableError,
  usePage,
} from "./api";
import { ComponentSpec } from "./components";
import { Component } from "./components/Component";
import { PageContext } from "./PageContext";
import {
  NOT_AUTHORIZED_COMPONENT,
  NOT_FOUND_COMPONENT,
} from "./components/standardcomponents";
import { PAGE_NORMAL } from "./sites/PageNormal";
import styled from "styled-components";
import { ErrorBoundary } from "react-error-boundary";

// Der CMS-Editor wird nachgeladen. Er ist das mit Abstand groesste Stueck der
// Anwendung - gemessen im Produktionsbau 495'928 Byte roh / 145'829 gzip,
// dazu chunk-shaka-player -, und ein anonymer Besucher braucht davon nichts.
//
// EIN "NOCHMAL VERSUCHEN" GIBT ES HIER BEWUSST NICHT - es koennte nicht
// wirken. Zwei Sperren liegen hintereinander:
//
//   1. React.lazy ruft die Ladefunktion genau EINMAL auf. Nach einem
//      Fehlschlag steht der Fehler im Payload und wird bei jedem weiteren
//      Rendern erneut geworfen, ohne die Funktion nochmals aufzurufen
//      (react 18.3.1, lazyInitializer: _status === Rejected -> throw). Die
//      liesse sich umgehen, indem man bei jedem Versuch ein FRISCHES lazy
//      baut.
//
//   2. Das nuetzt aber nichts, denn darunter liegt die Modulkarte des
//      Dokuments: eine Adresse, deren Abruf einmal fehlgeschlagen ist, bleibt
//      im selben Dokument als fehlgeschlagen vermerkt. Ein spaeteres
//      import() derselben Adresse gibt die alte Ablehnung zurueck und geht
//      GAR NICHT MEHR INS NETZ.
//
// Nachgemessen (Chrome, CDP, Netzmitschnitt): erster Versuch eine Anfrage
// nach chunk-EditPage, nach dem Zuruecksetzen der Boundary NULL weitere
// Anfragen - das frische lazy hat import() sehr wohl aufgerufen, der Browser
// hat es nur nicht mehr geholt. Ein Knopf, der nichts tut, ist schlimmer als
// kein Knopf: er laesst den Menschen dreimal klicken, bevor er das Richtige
// tut.
//
// Was WIRKLICH hilft, steht deshalb allein im Band: die Seite neu laden
// (frisches Dokument, frische Modulkarte - und nach einer Neuausgabe der
// Website ausserdem das einzige, was die neuen Dateinamen kennt), oder das
// Bearbeiten beenden und weiterlesen.
const EditPage = React.lazy(() => import("./EditPage"));

export default function RenderPage({ path }: { path: string }): JSX.Element {
  const pageState = usePage(path);

  const pageTitle = pageState.page?.title;
  useEffect(() => {
    if (pageTitle) {
      document.title = pageTitle + " - LWZO";
    }
  }, [pageTitle]);
  const user = useUser();
  const canEdit = !!pageState.page?.accessPolicy && user !== null;
  const [isEditing, setEditing] = useState(false);
  const contextValue = useMemo(
    () => ({
      isEditing: pageState.page !== null && isEditing,
      setEditing,
      canEdit,
      origin: window.location.origin,
    }),
    [pageState.page, isEditing, canEdit]
  );

  // Die Leseansicht der Seite - EINMAL gebaut, weil sie an drei Stellen
  // gebraucht wird: als normale Ansicht, als Platzhalter waehrend der Editor
  // nachgeladen wird, und unter dem Band, wenn er nicht ankam. Nebenbei
  // kostet sie so nur noch EINE geduldete Typmeldung statt zweier:
  // page.content ist ComponentSpec|null, PageComp verlangt ComponentSpec
  // (typecheck-baseline.txt, Einschraenkung der Union ComponentSpec).
  const leseAnsicht = pageState.page ? (
    <PageComp spec={pageState.page.content} />
  ) : null;

  let errorRender: JSX.Element | null = null;
  if (pageState.error instanceof UnavailableError) {
    errorRender = (
      <PageComp
        spec={makeSystemPage({
          type: NOT_FOUND_COMPONENT,
          path: path,
        })}
      />
    );
  }
  if (pageState.error instanceof NotFoundError) {
    errorRender = (
      <PageComp
        spec={makeSystemPage({
          type: NOT_FOUND_COMPONENT,
          path: path,
        })}
      />
    );
  }
  if (pageState.error instanceof PermissionDeniedError) {
    errorRender = (
      <PageComp
        spec={makeSystemPage({
          type: NOT_AUTHORIZED_COMPONENT,
          sufficientAMRs: [],
        })}
      />
    );
  }

  return (
    <PageContext.Provider value={contextValue}>
      {pageState.page ? (
        isEditing ? (
          /* EIGENE ERRORBOUNDARY, OBWOHL IN App.tsx SCHON EINE STEHT.

             Suspense faengt das Warten, nicht das Scheitern: kommt
             chunk-EditPage nicht an - Netz weg, oder die Website wurde
             frisch veroeffentlicht und die Datei heisst anders -, laeuft die
             Ablehnung nach oben durch. Die naechste Boundary waere die in
             src/App.tsx, und die ersetzt die GANZE Seite durch ihre
             Fehlermeldung.

             Das waere hier die falsche Antwort. Der Inhalt ist bereits
             geladen und anzeigbar; ausgefallen ist allein das Werkzeug zum
             Bearbeiten. Also bleibt die Seite stehen, und darueber steht,
             was fehlt. Die Boundary in components/Component.tsx hilft dabei
             nicht: die umschliesst einzelne Bauteile INNERHALB einer
             gerenderten Seite (und faengt so den Ausfall von
             chunk-shaka-player), sie liegt aber unterhalb dieser Stelle. */
          <ErrorBoundary
            // Der Pfad setzt zurueck, damit das Band nicht auf der naechsten
            // Seite stehen bleibt.
            resetKeys={[path]}
            fallbackRender={() => (
              <>
                <EditorNichtGeladen abbrechen={() => setEditing(false)} />
                {leseAnsicht}
              </>
            )}
          >
            <Suspense fallback={leseAnsicht}>
              <EditPage page={pageState.page} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          leseAnsicht
        )
      ) : (
        errorRender
      )}
    </PageContext.Provider>
  );
}

/**
 * Was zu sehen ist, wenn der CMS-Editor nicht geladen werden konnte.
 *
 * Bewusst ein SCHMALES Band ueber der weiterhin sichtbaren Seite und keine
 * ganzseitige Fehlerseite: der Inhalt ist da, nur das Bearbeiten geht nicht.
 * Wer die Seite gerade jemandem zeigt, verliert sie nicht wegen eines
 * Werkzeugs, das er im selben Moment gar nicht braucht.
 *
 * Angeboten werden nur die beiden Wege, die MESSBAR etwas bewirken (die
 * Begruendung, warum "Nochmal versuchen" nicht dabei ist, steht oben bei
 * EditPage): neu laden - das einzige, was nach einer frischen
 * Veroeffentlichung die neuen Dateinamen findet - oder weiterlesen.
 */
function EditorNichtGeladen(props: { abbrechen: () => void }): JSX.Element {
  return (
    <div
      className="alert alert-warning rounded-0 mb-0 d-flex flex-wrap align-items-center gap-2"
      role="alert"
      data-testid="cms-editor-failed"
    >
      <span className="flex-grow-1">
        <strong>Der Editor liess sich nicht laden.</strong> Die Seite selbst
        ist unverändert — nur das Bearbeiten steht gerade nicht zur Verfügung.
      </span>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        data-testid="cms-editor-reload"
        onClick={() => window.location.reload()}
      >
        Seite neu laden
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        data-testid="cms-editor-cancel"
        onClick={props.abbrechen}
      >
        Weiterlesen
      </button>
    </div>
  );
}

// Die Hoehe kommt jetzt aus der Seitenhuelle (.page-shell in main.css).
// Vorher stand hier min-height: calc(100vh - 141px) — 141px war die Hoehe der
// Navigationsleiste am Desktop. Auf dem Handy ist die obere Leiste
// ausgeblendet und die Leiste niedriger, dort blieb der Footer deshalb
// oberhalb der Falz stehen. Die Huelle rechnet mit keiner Hoehe.
const PageContainer = styled.div``;

export const PageComp = React.memo(
  (props: { spec: ComponentSpec }): JSX.Element => {
    return (
      <PageContainer>
        <Component spec={props.spec} />
      </PageContainer>
    );
  }
);

export function makeSystemPage(contentSpec: ComponentSpec): ComponentSpec {
  return {
    type: PAGE_NORMAL,
    content: {
      children: [contentSpec],
    },
  };
}
